import { useUserContext } from "@/contexts/UserContextProvider";
import { useState } from "react";
import { UserPlus, XIcon, Pencil } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogClose,
  DialogTitle,
} from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import Upload from "rc-upload";
import {
  uploadUserProfilePicture,
  removeUserProfilePicture,
} from "@/components/common/FileUpload/helpers";
import {
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuContent,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";

// 2mb
const ALLOWED_FILE_SIZE = 1024 * 1024 * 2;
const ALLOWED_FILE_EXTENSIONS = ["jpg", "png"];

const ProfilePictureUpload = () => {
  const { currentUser, refetchUser } = useUserContext();
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [selectedFile, setSelectedFile] = useState<File>();
  const [isUploading, setIsUploading] = useState<boolean>(false);

  const setPreview = (file: File) => {
    const fileNames = file.name.split(".");
    setErrorMessage("");
    if (file.size > ALLOWED_FILE_SIZE) {
      setErrorMessage("File size should be less than 2mb");
    } else if (
      !ALLOWED_FILE_EXTENSIONS.includes(
        fileNames[fileNames.length - 1].toLowerCase(),
      )
    ) {
      setErrorMessage("Only png and jpg files can be uploaded");
    } else {
      setSelectedFile(file);
    }
  };

  const onSubmit = async () => {
    if (!selectedFile) {
      return;
    }

    setIsUploading(true);
    try {
      await uploadUserProfilePicture(selectedFile);
      refetchUser();
      setSelectedFile(undefined);
      setShowUploadModal(false);
    } catch (e) {
      setIsUploading(false);
      setErrorMessage((e as Error).toString());
    }
  };

  return (
    <div className="flex items-center justify-center !min-w-11 !min-h-11 rounded-[10px] bg-accent text-sm font-medium text-muted-foreground cursor-pointer">
      {currentUser?.profilePictureUrl ? (
        <DropdownMenu modal>
          <DropdownMenuTrigger className="group relative w-11 h-11 cursor-pointer">
            <img
              src={currentUser.profilePictureUrl}
              alt="Profile"
              className="w-11 h-11 rounded-[10px] group-hover:opacity-0"
            />
            <Pencil className="absolute inset-0 m-auto w-5 h-5 opacity-0 group-hover:opacity-100" />
          </DropdownMenuTrigger>
          <DropdownMenuContent side="bottom">
            <DropdownMenuItem onClick={() => setShowUploadModal(true)}>
              Update
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={async () => {
                try {
                  await removeUserProfilePicture();
                  refetchUser();
                } catch (e) {
                  toast.error((e as Error).toString());
                }
              }}
            >
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : (
        <div className="group relative">
          <div className="w-11 h-11 rounded-[10px] bg-accent group-hover:opacity-0">
            <span className="flex items-center justify-center w-full h-full text-sm font-medium text-muted-foreground">
              {currentUser?.firstName?.charAt(0).toUpperCase()}
              {currentUser?.lastName?.charAt(0).toUpperCase()}
            </span>
          </div>
          <UserPlus
            className="absolute inset-0 m-auto w-5 h-5 opacity-0 group-hover:opacity-100"
            onClick={(e) => {
              e.stopPropagation();
              setShowUploadModal(true);
            }}
          />
        </div>
      )}
      {showUploadModal && (
        <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
          <DialogContent>
            <div className="flex items-center justify-between">
              <DialogTitle>Add profile picture:</DialogTitle>
              <DialogClose className="cursor-pointer">
                <XIcon />
              </DialogClose>
            </div>
            <Separator />
            <div className="flex flex-col gap-3 items-center">
              <Upload
                data={{ userId: currentUser?.id }}
                openFileDialogOnClick={!isUploading}
                onError={(message: string) => setErrorMessage(message)}
                customRequest={(values: { file: File }) =>
                  setPreview(values.file)
                }
                className="flex flex-col gap-3"
              >
                <div className="w-70 h-70 bg-transparent border hover:bg-accent flex flex-col items-center justify-center rounded-lg cursor-pointer">
                  {selectedFile ? (
                    <img
                      src={URL.createObjectURL(selectedFile)}
                      alt=""
                      className="w-full h-full object-cover rounded-lg"
                    />
                  ) : (
                    <>
                      <UserPlus className="w-30 h-30" />
                      <p className="text-sm text-secondary-foreground overflow-nowrap">
                        Click to upload or drag and drop
                      </p>
                    </>
                  )}
                </div>
              </Upload>
              <p>png, jpg (200px x 200px recommended)</p>
              {errorMessage && <p className="text-red-500">{errorMessage}</p>}
            </div>
            <Separator />
            <div className="flex justify-end items-center !p-0">
              <Button
                className="bg-link text-primary-foreground hover:bg-link hover:opacity-80"
                onClick={(e) => {
                  e.stopPropagation();
                  onSubmit();
                }}
                disabled={isUploading || !selectedFile}
              >
                Save
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default ProfilePictureUpload;
